import { type FC, type JSX } from "react";
import { Link, useParams } from "react-router-dom";

import { type OptionItem } from "@/api/admin";
import {
  FUNDING_APPLICATION_STATUS_MAP,
  type IFundingApplications,
  PayoutStatus,
  statusMapFunding,
} from "@/api/admin/fundings";
import { useCurrencyOptionsQuery } from "@/api/admin/useCurrencyQuery";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type SubMenuConfig,
} from "@/components/molecules";
import { CurrencySymbol } from "@/components/molecules/CurrencySymbol";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import { cn } from "@/lib/utils";
import type { PaginatedResponse } from "@/types/api";
import { FIELDS_MAPPING } from "@/utils/constants/allFieldsMapping";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";
import { FUNDING_APP_HASH } from "@/utils/constants/fundingApplicationHash";
import { formatEnumValue } from "@/utils/formatEnumValue";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { formatNumber } from "@/utils/formatNumber";
import { type IGroupParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";
import { parseDateRangeToParams } from "@/utils/parseDateRangeToParams";

import { EntityHistory } from "../../EntityHistory";
import {
  ApproveAction,
  RejectAction,
  useUpdateApplicationStatus,
} from "../../Funding";
import DropDownMenu from "../DropDownMenu";
import DynamicTable from "../DynamicTable";
import { type ColumnConfig } from "../type";

enum TableColumn {
  APPLICATION_ID = "Application ID",
  DATE_CREATED = "Date created",
  STATUS = "Status",
  REQUEST_AMOUNT = "Requested amount",
  RECEIVED_AMOUNT = "Received amount",
  TRANSFERED = "Transferred",
  BANK_TRANSFER_ID = "Bank transfer ID",
  ACTIONS = "",
}

const TABLE_HEADERS: TableHeaderType = Object.values(TableColumn).filter(
  header => header !== ""
);

interface FundingTableProps {
  tableData?: PaginatedResponse<IFundingApplications>;
  tableParams: IGroupParams;
  setTableParams: React.Dispatch<React.SetStateAction<IGroupParams>>;
  handleNext: () => void;
  handlePrev: () => void;
  searchValue: string;
  setSearchValue: (val: string) => void;
  isPending: boolean;
  addFundingButton?: JSX.Element;
  isFundingAction?: boolean;
  queryKey?: string | string[];
  isLinkToDetailsPage?: boolean;
  hasUpdatePermission?: boolean;
  isHistoryLog?: boolean;
  entityName?: string;
}

const FundingApplicationTable: FC<FundingTableProps> = ({
  tableData,
  tableParams,
  setTableParams,
  handleNext,
  handlePrev,
  searchValue,
  setSearchValue,
  isPending,
  addFundingButton,
  isFundingAction = false,
  queryKey,
  isLinkToDetailsPage = true,
  hasUpdatePermission = true,
  isHistoryLog = false,
  entityName = DEFAULT_NAME,
}) => {
  const { id = "" } = useParams();

  // Each row carries its own currency, so a Saudi and a Jordanian group render
  // correctly in the same table. Resolved once here rather than per row,
  // because a hook cannot be called inside a cell renderer.
  const symbolFor = useCurrencyOptionsQuery();

  const renderAmount = (amount: number, currency?: string | null) => {
    const { decimals, ...symbol } = symbolFor(currency);

    return formatNumber(
      amount,
      <CurrencySymbol {...symbol} classes="h-2.5" />,
      decimals
    );
  };

  const applicationPageHash = isFundingAction
    ? FUNDING_APP_HASH.group
    : FUNDING_APP_HASH.brand;

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const { dateFrom: fromDate, dateTo: toDate } = parseDateRangeToParams(
      selectedMap[TableColumn.DATE_CREATED]?.[0],
      true
    );

    const selectedStatuses =
      (selectedMap[TableColumn.STATUS] as string[] | undefined) ?? [];

    const rawStatuses = selectedStatuses
      .map(status => statusMapFunding[status])
      .filter(Boolean);

    setTableParams(prev => ({
      ...prev,
      fromDate,
      toDate,
      status: rawStatuses.length ? rawStatuses : undefined,
      offset: 0,
    }));
  };

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    [TableColumn.STATUS]: Object.keys(statusMapFunding),
    [TableColumn.DATE_CREATED]: [],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    [TableColumn.STATUS]: { type: "status" },
    [TableColumn.DATE_CREATED]: { type: "date" },
  };

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const hasFilters =
    searchValue ||
    (tableParams.status as string[])?.length ||
    tableParams.fromDate ||
    tableParams.toDate;

  const ActionsCell: FC<{
    id: string;
    status: PayoutStatus;
    isLast: boolean;
    queryKey?: string | string[];
  }> = ({ id, status, isLast, queryKey }) => {
    const { handleChangeStatus, isPending } = useUpdateApplicationStatus(
      id,
      queryKey
    );

    const classes =
      "px-2 py-1.5 w-full text-xs rounded-[6px] focus:bg-accent hover:bg-accent focus:text-accent-foreground";

    const disabledClasses =
      "opacity-[40%] hover:bg-transparent cursor-not-allowed";

    const isPendingStatus = status === PayoutStatus.PENDING;
    const canApprove = isPendingStatus;
    const canReject = isPendingStatus;

    return (
      <TableCellText classes="w-[4%]" last={isLast}>
        <DropDownMenu
          classes="w-full"
          menuList={[
            {
              dialog: (
                <ApproveAction
                  classes={cn(classes, !canApprove && disabledClasses)}
                  isPending={isPending}
                  onAction={handleChangeStatus}
                  disabled={!canApprove}
                />
              ),
            },
            ...(canReject
              ? [
                  {
                    dialog: (
                      <RejectAction
                        classes={classes}
                        isPending={isPending}
                        onAction={handleChangeStatus}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </TableCellText>
    );
  };

  const actionColumn: ColumnConfig<IFundingApplications> | null =
    isFundingAction && hasUpdatePermission
      ? ({
          key: "actions",
          label: TableColumn.ACTIONS,
          visible: true,
          headerComponent: ({ label }: { label: string }) => (
            <TableHeadCellCustom>{label}</TableHeadCellCustom>
          ),
          cellRenderer: (row: IFundingApplications, isLast: boolean) => (
            <ActionsCell
              id={row.id}
              status={row.status}
              isLast={isLast}
              queryKey={queryKey}
            />
          ),
        } as unknown as ColumnConfig<IFundingApplications>)
      : null;

  const columns: ColumnConfig<IFundingApplications>[] = [
    {
      key: "referenceNumber",
      label: TableColumn.APPLICATION_ID,
      visible: flags[TableColumn.APPLICATION_ID],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) =>
        isLinkToDetailsPage ? (
          <TableCellLink last={isLast}>
            <Link
              to={`${PATHS.ADMIN.APPLICATION(row.id)}${applicationPageHash}`}
              state={{ from: `${location.pathname}#funding:applications` }}
              data-testid={`funding-application-id-${row.referenceNumber}`}
            >
              {row.referenceNumber}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{row.referenceNumber}</TableCellText>
        ),
    },
    {
      key: "createdAt",
      label: TableColumn.DATE_CREATED,
      visible: flags[TableColumn.DATE_CREATED],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.createdAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "status",
      label: TableColumn.STATUS,
      visible: flags[TableColumn.STATUS],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        return row.status ? (
          <TableCellBadge
            last={isLast}
            stage={
              FUNDING_APPLICATION_STATUS_MAP[formatEnumValue(row?.status)]
                ?.badge
            }
          >
            {formatEnumValue(row?.status) ?? "-"}
          </TableCellBadge>
        ) : (
          <TableCellText last={isLast}>-</TableCellText>
        );
      },
    },
    {
      key: "requestedAmount",
      label: TableColumn.REQUEST_AMOUNT,
      visible: flags[TableColumn.REQUEST_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {renderAmount(row?.requestedAmount, row?.currency)}
        </TableCellText>
      ),
    },
    {
      key: "receivedAmount",
      label: TableColumn.RECEIVED_AMOUNT,
      visible: flags[TableColumn.RECEIVED_AMOUNT],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row?.receivedAmount
            ? renderAmount(row?.receivedAmount, row?.currency)
            : "-"}
        </TableCellText>
      ),
    },
    {
      key: "isTransferredFromBank",
      label: TableColumn.TRANSFERED,
      visible: flags[TableColumn.TRANSFERED],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.isTransferredFromBank === undefined
            ? "-"
            : row.isTransferredFromBank
              ? "Yes"
              : "No"}
        </TableCellText>
      ),
    },
    {
      key: "bankTransferId",
      label: TableColumn.BANK_TRANSFER_ID,
      visible: flags[TableColumn.BANK_TRANSFER_ID],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row?.bankTransferId || "-"}
        </TableCellText>
      ),
    },
    ...(actionColumn ? [actionColumn] : []),
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={FUNDING_APPLICATION_STATUS_MAP}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        {isHistoryLog && (
          <EntityHistory
            entityId={id}
            entityName={entityName}
            blockName="Funding"
            mapKeyToName={FIELDS_MAPPING.fundingTransferDetails}
          />
        )}
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
        {addFundingButton}
      </FilterSearch>

      {isPending ? (
        <Loader />
      ) : (
        <DynamicTable<IFundingApplications>
          data={tableData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={tableData}
          setTableParams={setTableParams}
          notFoundTitle={
            hasFilters ? "No records match your filters." : "No records found"
          }
        />
      )}
    </>
  );
};

export default FundingApplicationTable;
